import type { ContentRefKind } from '@gitbook/app-content-refs';
import { canInsertInlineIn, INLINE_MENTION } from '@gitbook/doc-core';
import type { InlineProps, Plugin } from '@gitbook/slate-react';

import type { CreateInputContentRefPaletteOptions } from '../../ContentReferences';
import { createTypePlugin } from '../type-plugin';
import { InlineMention } from './InlineMention';
import { MentionPicker } from './MentionPicker';
import { ALLOWED_CONTENT_REFS_IN_INLINE_MENTION } from './util';

const MENTION_CONTEXT_TYPE = 'mentionContext';

/**
 * Slate plugin to detect user typing "@" or "#".
 */
export const mentionPlugin: Plugin = createMentionPlugin({
    accept: {
        '@': ALLOWED_CONTENT_REFS_IN_INLINE_MENTION,
        '#': ALLOWED_CONTENT_REFS_IN_INLINE_MENTION,
    },
});

export function createMentionPlugin(
    options: Omit<CreateInputContentRefPaletteOptions, 'accept' | 'search'> & {
        accept: {
            [symbol: string]: ContentRefKind[];
        };
    }
): Plugin {
    const { accept, ...restOptions } = options;
    const typePlugin = createTypePlugin({
        markType: MENTION_CONTEXT_TYPE,
        symbols: Object.keys(options.accept),
        getData: ({ value, symbol, textValue }) => {
            const { document, startBlock } = value;
            if (!startBlock) {
                return null;
            }
            if (!canInsertInlineIn({ document, block: startBlock }, INLINE_MENTION)) {
                return null;
            }
            return { symbol, value: textValue };
        },
        renderMark: (props) => {
            const { mark } = props;
            const acceptForSymbol = options.accept[mark.data.get('symbol')];

            if (!acceptForSymbol) {
                return null;
            }
            return (
                <MentionPicker
                    {...props}
                    options={{
                        ...restOptions,
                        accept: acceptForSymbol,
                    }}
                />
            );
        },
    });

    const renderNode = (props: InlineProps) => {
        const { node } = props;

        switch (node.type) {
            case INLINE_MENTION:
                return <InlineMention {...props} />;
        }
    };

    return {
        ...typePlugin,
        renderNode,
    };
}
